
import { useState, useRef } from "react";
import { Upload, X, Image as ImageIcon, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
// Image removed - use img tag;
import toast from "react-hot-toast";
import { storageApi } from "@/lib/api";

interface UploadedFile {
  url: string;
  s3_key: string;
  file: File;
  preview?: string;
}

interface FileUploadProps {
  onUploadComplete: (files: UploadedFile[]) => void;
  multiple?: boolean;
  maxFiles?: number;
  accept?: string;
  productId?: number | null;
  className?: string;
}

export default function FileUpload({
  onUploadComplete,
  multiple = true,
  maxFiles = 10,
  accept = "image/*",
  productId = null,
  className = "",
}: FileUploadProps) {
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ [key: string]: number }>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const filesArray = Array.from(files);
    const remainingSlots = maxFiles - uploadedFiles.length;

    if (filesArray.length > remainingSlots) {
      toast.error(`You can only upload ${remainingSlots} more file(s)`);
      return;
    }

    setUploading(true);

    try {
      const uploadPromises = filesArray.map(async (file) => {
        // Validate file type
        if (!file.type.startsWith("image/")) {
          throw new Error(`${file.name} is not an image file`);
        }

        // Validate file size (10MB max)
        const maxSize = 10 * 1024 * 1024; // 10MB
        if (file.size > maxSize) {
          throw new Error(`${file.name} exceeds 10MB size limit`);
        }

        // Create preview URL
        const preview = URL.createObjectURL(file);

        // Get presigned URL from backend
        const uploadData = await storageApi.getUploadUrl({
          file_name: file.name,
          file_type: file.type,
          file_size: file.size,
          product_id: productId,
        });

        // Upload to S3 using presigned POST
        const formData = new FormData();
        
        // Add all fields from presigned POST (these include policy, signature, etc.)
        Object.entries(uploadData.upload_fields).forEach(([key, value]) => {
          formData.append(key, value as string);
        });
        
        // Add the file - the key name should match what's in the presigned POST fields
        // Usually it's "file" or matches the S3 key name
        // Check if 'key' is in fields, if so use that, otherwise use 'file'
        const fileFieldName = uploadData.upload_fields.key ? "file" : "file";
        formData.append(fileFieldName, file);

        // Upload to S3
        const uploadResponse = await fetch(uploadData.upload_url, {
          method: "POST",
          body: formData,
        });

        if (!uploadResponse.ok) {
          throw new Error(`Failed to upload ${file.name}`);
        }

        // Notify backend that upload is complete
        await storageApi.uploadComplete({
          s3_key: uploadData.s3_key,
          product_id: productId,
          alt_text: file.name,
        });

        return {
          url: uploadData.public_url || uploadData.upload_url,
          s3_key: uploadData.s3_key,
          file,
          preview,
        };
      });

      const uploaded = await Promise.all(uploadPromises);
      const newFiles = [...uploadedFiles, ...uploaded];
      setUploadedFiles(newFiles);
      onUploadComplete(newFiles);
      toast.success(`Successfully uploaded ${uploaded.length} file(s)`);
    } catch (error: any) {
      console.error("Upload error:", error);
      toast.error(error.message || "Failed to upload files");
    } finally {
      setUploading(false);
      setUploadProgress({});
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const removeFile = (index: number) => {
    const file = uploadedFiles[index];
    if (file.preview) {
      URL.revokeObjectURL(file.preview);
    }
    const newFiles = uploadedFiles.filter((_, i) => i !== index);
    setUploadedFiles(newFiles);
    onUploadComplete(newFiles);
  };

  return (
    <div className={className}>
      {/* Upload Area */}
      <div
        onClick={() => !uploading && fileInputRef.current?.click()}
        className={`relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all ${
          uploading
            ? "border-purple-400 bg-purple-50 cursor-not-allowed"
            : "border-gray-300 hover:border-purple-500 hover:bg-purple-50"
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={handleFileSelect}
          className="hidden"
          disabled={uploading}
        />
        {uploading ? (
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
            <p className="text-sm text-gray-600">Uploading files...</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <div className="p-3 rounded-full bg-gradient-to-br from-purple-500 to-pink-500">
              <Upload className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-700">
                Click to upload or drag and drop
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {accept === "image/*" ? "Images" : "Files"} up to 10MB
                {multiple && ` (Max ${maxFiles} files)`}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Uploaded Files Preview */}
      <AnimatePresence>
        {uploadedFiles.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-4 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3"
          >
            {uploadedFiles.map((file, index) => (
              <motion.div
                key={file.s3_key}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                className="relative group aspect-square rounded-lg overflow-hidden bg-gray-100 border border-gray-200"
              >
                {file.preview || file.url ? (
                  <img
                    src={file.preview || file.url}
                    alt={file.file.name}
                    
                    className="object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <ImageIcon className="w-8 h-8 text-gray-400" />
                  </div>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFile(index);
                  }}
                  className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="w-3 h-3" />
                </button>
                {uploadProgress[file.s3_key] !== undefined && (
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-200">
                    <div
                      className="h-full bg-purple-600 transition-all"
                      style={{ width: `${uploadProgress[file.s3_key]}%` }}
                    />
                  </div>
                )}
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
